import React, { FC, Fragment, useEffect } from 'react';
import { useMutation, NetworkStatus } from '@apollo/client';
import {
  Box,
  ButtonAppearance,
  FormControl,
  Radio,
  Set,
  SetDirection,
  SetSpacing,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSubHeader,
  Spinner,
  Text,
  TextArea,
  SpinnerSize,
} from '@bechtel/gds';
import { AttachmentMetadataInput, ProjectMilestoneTypeRejectionReason, RejectMilestoneInput } from '@bechtel/types';
import { Field, FieldArray, Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import produce from 'immer';
import { capitalize, get, map, startCase } from 'lodash';
import { array, object, string } from 'yup';
import {
  FileUpload,
  FileUploadInput,
  FileUploadPreviewImg,
  FileUploadResponse,
  DrawerFooterButtonStyled,
  TitleValue,
  ListSelectionStyled,
  ErrorRetry,
  useProjectMilestoneTypeReasonsQuery,
} from '@bechtel/client-common';
import { DELETE_BLOCK_MEDIA } from '@bechtel/client-gql';
// TODO: refactor BlockHeadingStyled out into a sidesheet export
import { BlockHeadingStyled } from './styled';
import { useCommodity } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import { MilestoneError } from '../error';
import { useRejectMilestone } from '../hooks/use-reject-milestone';
import { RejectMilestoneProps } from './types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { clearCacheByKey, QueryKeys } from '~/services/cache';

interface FormikState {
  comments: RejectMilestoneInput['comments'];
  rejectionReasonTypeId: RejectMilestoneInput['rejectionReasonTypeId'];
  mediaMetadata?: RejectMilestoneInput['attachmentMetadata'];
}

const initialValues: FormikState = {
  rejectionReasonTypeId: '',
  comments: '',
  mediaMetadata: [],
};

const rejectMilestoneValidationSchema = object().shape({
  rejectionReasonTypeId: string().required(t`Select a rejection type`),
  comments: string()
    .min(2, t`Too Short!`)
    .required(t`Please explain what was the issue`),
  mediaMetadata: array()
    .required()
    .max(3, t`Max 3 photos can be uploaded`),
});

const RejectMilestone: FC<RejectMilestoneProps> = ({ onClose, milestone, refetchQueries, onActionCompleted }) => {
  const { i18n } = useLingui();
  const { tag: workPackageTag } = useWorkPackage();
  const [rejectMilestone, { loading, data, error }] = useRejectMilestone(onActionCompleted);
  const { tag: commodityTag, typeName: commodityType } = useCommodity();
  const [deleteBlockMedia, { loading: deleteLoading, data: deleteData }] = useMutation(DELETE_BLOCK_MEDIA);
  const deleteError = get(deleteData, 'deleteBlockMedia.error', null);
  const milestoneError = get(data, 'rejectMilestone.error') || error;
  const [
    getProjectMilestoneTypeRejectionReasons,
    {
      data: reasonsData,
      loading: reasonsLoading,
      error: reasonsError,
      refetch: reasonsRefetch,
      networkStatus: reasonsNetworkStatus,
    },
  ] = useProjectMilestoneTypeReasonsQuery();

  useEffect(() => {
    if (!milestone || !milestone.type || !milestone.type.id) return;
    getProjectMilestoneTypeRejectionReasons({ variables: { input: { projectMilestoneTypeId: milestone.type.id } } });
  }, [milestone]);

  const projectMilestoneTypeRejectionReasons = get(reasonsData, 'projectMilestoneTypeRejectionReasons', []);

  return (
    <>
      {milestoneError && <MilestoneError milestoneError={milestoneError} />}
      <SideSheet
        visible
        onScroll={(e) => {
          e.stopPropagation();
        }}
      >
        <SideSheetHeader title={t`Reject Claim`}>
          <SideSheetClose onClick={onClose} disabled={loading}></SideSheetClose>
        </SideSheetHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={rejectMilestoneValidationSchema}
          onSubmit={async (values: FormikState, actions: FormikActions<FormikState>) => {
            const { data: rejectData } = await rejectMilestone({
              variables: {
                input: {
                  commodityMilestoneId: milestone.id,
                  comments: values.comments,
                  rejectionReasonTypeId: values.rejectionReasonTypeId,
                  attachmentMetadata: values.mediaMetadata,
                },
              },
              update: (cache, result) => {
                if (result.data && result.data.rejectMilestone.commodity) {
                  clearCacheByKey(cache, QueryKeys.EARNABLE_HOURS);
                }
              },
              refetchQueries: refetchQueries,
            });
            actions.resetForm({ values: initialValues });
            actions.setSubmitting(false);
            if (!get(rejectData, 'rejectMilestone.error')) onClose();
          }}
        >
          {({ submitForm, errors, values, setFieldValue, touched, isValid, dirty }: FormikProps<FormikState>) => {
            return (
              <Fragment>
                <SideSheetSubHeader>
                  <Set direction={SetDirection.COL} spacing={SetSpacing.COMPACT}>
                    <TitleValue title={t`Work Package`} value={workPackageTag}></TitleValue>
                    <TitleValue title={commodityType} value={commodityTag}></TitleValue>
                    <TitleValue title={t`Milestone`} value={milestone.name}></TitleValue>
                  </Set>
                </SideSheetSubHeader>
                <SideSheetBody>
                  <SideSheetContentCol>
                    <BlockHeadingStyled>
                      <Trans>Rejection Type (Select One)</Trans>
                    </BlockHeadingStyled>
                    {(reasonsLoading || reasonsNetworkStatus === NetworkStatus.refetch) && (
                      <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }}>
                        <Spinner size={SpinnerSize.LARGE} />
                      </Box>
                    )}
                    {!reasonsLoading && reasonsError && (
                      <div>
                        <ErrorRetry refetch={reasonsRefetch}>
                          <Trans>
                            There was a problem loading Rejection Reasons. Please click the button below to try again.
                          </Trans>
                        </ErrorRetry>
                      </div>
                    )}
                    {!reasonsLoading && !reasonsError && (
                      <FormControl
                        label=""
                        error={
                          touched.rejectionReasonTypeId && errors.rejectionReasonTypeId
                            ? i18n._(errors.rejectionReasonTypeId)
                            : ''
                        }
                      >
                        {projectMilestoneTypeRejectionReasons.map(
                          (rejectionType: ProjectMilestoneTypeRejectionReason) => {
                            return (
                              <ListSelectionStyled
                                key={rejectionType.id}
                                onClick={() => {
                                  setFieldValue('rejectionReasonTypeId', rejectionType.id);
                                }}
                              >
                                <Radio
                                  name="rejectionReasonTypeId"
                                  id={rejectionType.id}
                                  checked={values.rejectionReasonTypeId === rejectionType.id}
                                  readOnly
                                />
                                <Box ml={{ sm: '500' }}>
                                  <Text size="400">{startCase(capitalize(rejectionType.reason))}</Text>
                                </Box>
                              </ListSelectionStyled>
                            );
                          }
                        )}
                      </FormControl>
                    )}

                    <Box px={{ sm: '500' }} my={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
                      <FormControl
                        label={t`Comments (Required)`}
                        css={`
                          flex-grow: 1;
                        `}
                        error={touched.comments && errors.comments ? i18n._(errors.comments) : ''}
                      >
                        <Field name="comments">
                          {({ field }) => {
                            return <TextArea maxLength={2000} {...field} rows="4" placeholder={t`Add a Comment`} />;
                          }}
                        </Field>
                      </FormControl>
                    </Box>
                    <Box px={{ sm: '500' }} my={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
                      <FieldArray name="mediaMetadata">
                        {({ form, push, remove }) => {
                          return (
                            <FormControl
                              label={t`Upload a Photo (${
                                form.values.mediaMetadata.length ? form.values.mediaMetadata.length : 0
                              }/3)`}
                              css="flex-grow: 1;"
                            >
                              {!deleteError ? (
                                <FileUpload
                                  filesCount={form.values.mediaMetadata.length ? form.values.mediaMetadata.length : 0}
                                  maxFiles={3}
                                  maxFileSizeMB={10}
                                  acceptFileTypes="image/jpeg, image/png"
                                  onSuccess={(res: FileUploadResponse, file) => {
                                    const newMediaData: AttachmentMetadataInput = {
                                      fileName: file.name,
                                      fileSize: file.size,
                                      attachmentId: res.attachmentId,
                                      mimeType: file.type,
                                    };

                                    push(newMediaData);
                                  }}
                                >
                                  {({ getInputProps, getRootProps, loading, files, setFiles, acceptFileTypes }) => {
                                    return (
                                      <Fragment>
                                        <FileUploadInput
                                          getInputProps={getInputProps}
                                          getRootProps={getRootProps}
                                          loading={loading || deleteLoading}
                                          acceptFileTypes={acceptFileTypes}
                                        />
                                        {deleteError && (
                                          <Box my={{ sm: '200' }}>
                                            <Trans>There was an error when deleting a file.</Trans>
                                          </Box>
                                        )}
                                        {map(form.values.mediaMetadata, (file, index: number) => {
                                          return (
                                            <Box
                                              mb={{ sm: '400' }}
                                              key={`${file.name}.${index}`}
                                              flexDirection={{ sm: 'column' }}
                                            >
                                              <FileUploadPreviewImg
                                                file={files[index]}
                                                onDelete={async () => {
                                                  const { data } = await deleteBlockMedia({
                                                    variables: {
                                                      input: {
                                                        attachmentId: files[index].attachmentId,
                                                      },
                                                    },
                                                  });

                                                  if (!data.deleteBlockAttachment.error) {
                                                    remove(index);
                                                    setFiles(
                                                      produce((draft) => {
                                                        //@ts-ignore splice will exist on draft since it is an array
                                                        draft.splice(index, 1);
                                                      })
                                                    );
                                                  }
                                                }}
                                              />
                                            </Box>
                                          );
                                        })}
                                      </Fragment>
                                    );
                                  }}
                                </FileUpload>
                              ) : (
                                <div>
                                  <Trans>There was an error when deleting the file</Trans>
                                </div>
                              )}
                            </FormControl>
                          );
                        }}
                      </FieldArray>
                    </Box>
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={onClose}
                        disabled={loading}
                      >
                        <Trans>Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={submitForm}
                        disabled={!dirty || !isValid || loading}
                      >
                        {loading && <Spinner />}
                        {!loading && <Trans>Confirm</Trans>}
                      </DrawerFooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </Fragment>
            );
          }}
        </Formik>
      </SideSheet>
    </>
  );
};

export default RejectMilestone;
